import { useCallback, useEffect, useState } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'bokapsys-theme'

function readPref(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    /* ignore */
  }
  return 'system'
}

function systemTheme(): 'light' | 'dark' {
  return typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function effective(pref: ThemePreference): 'light' | 'dark' {
  return pref === 'system' ? systemTheme() : pref
}

function apply(pref: ThemePreference) {
  document.documentElement.setAttribute('data-theme', effective(pref))
}

export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(() => readPref())

  // Apply on mount + whenever preference changes.
  useEffect(() => {
    apply(preference)
    try {
      localStorage.setItem(STORAGE_KEY, preference)
    } catch {
      /* ignore */
    }
  }, [preference])

  // Re-apply when system preference changes (only when user picked 'system').
  useEffect(() => {
    if (preference !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => apply('system')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [preference])

  const setTheme = useCallback((next: ThemePreference) => setPreference(next), [])

  return { preference, theme: effective(preference), setTheme }
}
